import { Box, List, ListItem } from '@mui/material';
import React from 'react';
import KPIResult from './KPIResult';
import { KPI, Resource } from '@esg/esg-global-types';
import { ResultDetailExtended } from '../../../lib/readiness_gauge/readiness_gauge_instance';
import FilledContainer from '../../shared/container/FilledContainer';
import { Heading } from '../../shared/typography/PrimaryTypography';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';

export interface CategoryResults {
  name: string;
  kpi_details: Array<ResultDetailExtended>;
}

interface CategoryResultsSectionProps {
  results: CategoryResults;
  loading: boolean;
  gri_resources: Array<Resource>;
  sdg_resources: Array<Resource>;
}

/**
 * A wrapper component to list KPI details relevant to the chosen ESG category.
 * @param {string} name Name of ESG category.
 * @param {Array<ResultDetailExtended>} kpi_details List of KPI Detail objects to display.
 * @param {boolean} loading Flag for results data loading from parent.
 * @param {Array<Resource>} gri_resources List of GRI related Resource Centre resources for responses to search through for relevant compliance reference link.
 * @param {Array<Resource>} sdg_resources List of SDG related Resource Centre resources for responses to search through for relevant sdg reference links.
 * @returns {JSX.Element}
 */
const CategoryResultsSection = ({
  loading,
  results,
  gri_resources,
  sdg_resources
}: CategoryResultsSectionProps) => {
  const [expandedKPI, setExpandedKPI] = React.useState<KPI | null>(null);

  const handleToTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleChangeExpanded = (kpi: KPI, expanded: boolean): void => {
    setExpandedKPI(expanded ? kpi : null);
  };

  return (
    <Box>
      {loading ? (
        <PrimarySkeleton variant="rounded" height={50} width={300} sx={{ m: 2 }} />
      ) : (
        <Heading weight="Bold" sx={{ m: 2, textAlign: 'left' }}>
          {handleToTitleCase(results.name)}
        </Heading>
      )}
      <FilledContainer
        sx={{
          my: 2,
          mx: 2
        }}
      >
        {loading ? (
          <PrimarySkeleton height={200} variant="rounded" />
        ) : (
          <List
            sx={{
              overflow: 'auto',
              maxHeight: '70vh'
            }}
          >
            {results.kpi_details.map((kpi_detail: ResultDetailExtended) => (
              <ListItem key={kpi_detail.kpi.id}>
                <KPIResult
                  kpi_detail={kpi_detail}
                  expanded={!!expandedKPI && expandedKPI.id === kpi_detail.kpi.id}
                  gri_resources={gri_resources}
                  sdg_resources={sdg_resources}
                  handleChangeExpanded={(kpi: KPI, expanded: boolean) =>
                    handleChangeExpanded(kpi, expanded)
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </FilledContainer>
    </Box>
  );
};

export default CategoryResultsSection;
